import { getTimeRemainingSecond } from "../components/auctionLots/auctionBidTimeFormat";
import { getAvailableMaxBids } from "../components/auctionLots/getAvailableMaxBids";
import currenciesService from "../currenciesService";
import events from "../events";
import getAuctionHub from "../getAuctionHub";
import { getLiveAuctionUrl, liveAuctionSchedule } from "../live-auction/liveAuctionSchedule";
import { formatDateTime, formatDateUtc } from "../utils/formatDate";
import formatPrice, { formatForeignCurrency } from "../utils/formatPrice";
import { auctionV2ApiClient } from "./AuctionApiClient";
import { AuctionClassTypes, AuctionItemStatus, AuctionStatus } from "./contracts";
const TimerHoursTreshold = 24;
export function auctionBid(data) {
    let animationFrameRef = 0;
    let latestStartTime = -1;
    let currentTimeSec = 0;
    // The lot would show a countdown, but its auction is registered for the live site: it comes back when the
    // registration goes away.
    let timerHeldBack = false;
    return {
        $el: {},
        auctionItem: {},
        chosenMaxBid: null,
        isLoading: false,
        modalOpen: false,
        isLoadingAddToWatch: false,
        timeRemaining: [],
        showTimer: false,
        errorMessage: "",
        availableMaxBids: [],
        formatPrice,
        formatDateUtc,
        formatDateTime,
        foreignCurrency: currenciesService.getRate(),
        // The state of the live auctions is known, so it is known whether bidding is allowed here.
        liveScheduleReady: false,
        // The auction's live session on live.skanfil.no is running: bidding is replaced by a link to the live site.
        isLiveAuction: false,
        // The auction is registered for the live site and has not ended: its lots show no countdown.
        isRegisteredAuction: false,
        init() {
            this.auctionItem = JSON.parse(this.$el.closest("[data-product]").dataset.product);
            this.syncLiveAuction();
            this.fillAvailableMaxBids();
            this.updateTimer();
            events.onCurrencyChanged.subscribe((currency) => {
                this.foreignCurrency = currency;
            });
            liveAuctionSchedule.subscribe(() => this.syncLiveAuction());
            // When the live auction of this lot ends, the lot fetches itself again, exactly as it does when its own
            // countdown reaches zero. The live auction link stays until this is done.
            liveAuctionSchedule.watch(this.auctionItem.auctionId, () => this.refreshAfterAuctionEnded());
            getAuctionHub().then((hub) => {
                hub === null || hub === void 0 ? void 0 : hub.watchLot(this.auctionItem.id, this.bidPlacedHandler.bind(this), this.lotUpdatedHandler.bind(this));
            });
        },
        /**
         * Decides whether the lot counts down, from its current data, and starts the countdown.
         */
        updateTimer() {
            var _a;
            window.cancelAnimationFrame(animationFrameRef);
            latestStartTime = -1;
            const secondsTillEnd = (_a = this.auctionItem.secondsTillEnd) !== null && _a !== void 0 ? _a : 0;
            const hours = secondsTillEnd / 3600;
            const wouldCountDown = this.isBiddable &&
                hours < TimerHoursTreshold &&
                !this.auctionItem.isAuctionHallItem &&
                !this.isCombinedType;
            // Nobody knows how long the live auction lasts, so there is nothing to count down to.
            timerHeldBack = wouldCountDown && this.isRegisteredAuction;
            this.showTimer = wouldCountDown && !this.isRegisteredAuction;
            if (this.showTimer && secondsTillEnd > 0) {
                animationFrameRef = window.requestAnimationFrame(this.setTimeRemaining.bind(this));
            }
        },
        /**
         * The registration went away while the page was open. The countdown starts from the time left now, so the lot
         * is read again (the data it was loaded with is as old as the page).
         */
        async restoreTimer() {
            var _a;
            try {
                this.auctionItem = await auctionV2ApiClient.getItem(this.auctionItem.id);
            }
            catch (error) {
                console.error(error);
                const endsAt = Date.parse((_a = this.auctionItem.endAt) !== null && _a !== void 0 ? _a : "");
                if (!isNaN(endsAt)) {
                    this.auctionItem = { ...this.auctionItem, secondsTillEnd: Math.max(0, (endsAt - Date.now()) / 1000) };
                }
            }
            this.timeRemaining = [];
            this.fillAvailableMaxBids();
            this.updateTimer();
        },
        async refreshAfterAuctionEnded() {
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    const data = await auctionV2ApiClient.getItem(this.auctionItem.id);
                    this.timeRemaining = [];
                    this.auctionItem = data;
                    this.showTimer = false;
                    // The lot is up to date, and a finished lot has no countdown to bring back.
                    timerHeldBack = false;
                    latestStartTime = -1;
                    this.fillAvailableMaxBids();
                    return;
                }
                catch (error) {
                    console.error(error);
                }
            }
        },
        syncLiveAuction() {
            this.liveScheduleReady = liveAuctionSchedule.isReady();
            this.isLiveAuction = liveAuctionSchedule.isLive(this.auctionItem.auctionId);
            this.isRegisteredAuction = liveAuctionSchedule.isRegistered(this.auctionItem.auctionId);
            if (this.isRegisteredAuction && this.showTimer) {
                // It was registered while the page was open.
                this.showTimer = false;
                timerHeldBack = true;
                window.cancelAnimationFrame(animationFrameRef);
            }
            else if (!this.isRegisteredAuction && timerHeldBack) {
                // The registration was deleted while the page was open.
                timerHeldBack = false;
                void this.restoreTimer();
            }
        },
        async bidPlacedHandler(data) {
            this.auctionItem = await auctionV2ApiClient.getItem(data.id);
            this.isLoading = false;
            // Clear latest start time so in next animation frame we'll get new time
            latestStartTime = -1;
            this.fillAvailableMaxBids();
        },
        async lotUpdatedHandler(data) {
            this.auctionItem = {
                ...this.auctionItem,
                ...data,
            };
            // Clear latest start time so in next animation frame we'll get new time
            latestStartTime = -1;
        },
        setTimeRemaining(timeStamp) {
            if (this.auctionItem.secondsTillEnd == null)
                return;
            // is it the first iteration in this cycle?
            if (latestStartTime == -1) {
                this.timeRemaining = getTimeRemainingSecond(this.auctionItem.secondsTillEnd);
                latestStartTime = timeStamp + (this.auctionItem.secondsTillEnd * 1000);
            }
            const currentTimeMs = latestStartTime - timeStamp;
            if (currentTimeMs <= 0) {
                window.cancelAnimationFrame(animationFrameRef);
                const that = this;
                auctionV2ApiClient.getItem(this.auctionItem.id).then(data => {
                    that.timeRemaining = [];
                    that.auctionItem = data;
                    that.showTimer = false;
                });
                return;
            }
            // the time to display now
            const timeSec = Math.floor(currentTimeMs / 1000);
            if (currentTimeSec != timeSec) {
                const newTime = getTimeRemainingSecond(timeSec);
                this.updateTimerDOM(newTime);
                currentTimeSec = timeSec;
            }
            animationFrameRef = window.requestAnimationFrame(this.setTimeRemaining.bind(this));
        },
        updateTimerDOM(newTime) {
            const hoursEl = this.$el.querySelector('.time-hours');
            const minutesEl = this.$el.querySelector('.time-minutes');
            const secondsEl = this.$el.querySelector('.time-seconds');
            if (hoursEl && minutesEl && secondsEl) {
                hoursEl.textContent = newTime[0] || '';
                minutesEl.textContent = newTime[1] || '';
                secondsEl.textContent = newTime[2] || '';
            }
        },
        bidBtnClicked() {
            if (this.isAftersale) {
                this.openBuyNowModal();
            }
            else {
                this.bid();
            }
        },
        async bid() {
            var _a;
            this.errorMessage = "";
            // Last line of defence: a lot of a started live auction is never bid on from skanfil.no.
            if (liveAuctionSchedule.isLive(this.auctionItem.auctionId))
                return;
            if (!this.chosenMaxBid)
                return;
            if (!document.body.dataset.username) {
                events.openLogin.trigger({ open: true, centeredFixed: true });
                return;
            }
            if (this.auctionItem.bidsCount > 0 &&
                parseInt(this.chosenMaxBid) <= this.auctionItem.currentBidPrice) {
                this.errorMessage =
                    "Budet ditt må være større enn det nåværende budet";
                return;
            }
            this.isLoading = true;
            try {
                const maximumAmount = parseInt(this.chosenMaxBid);
                this.chosenMaxBid = null;
                // auctionItem will be refreshed on BidPlaced message from SignalR
                await auctionV2ApiClient.bidLot(this.auctionItem.id, {
                    maximumAmount,
                });
                // Clear latest start time so in next animation frame we'll get new time
                latestStartTime = -1;
                (_a = window.gtag) === null || _a === void 0 ? void 0 : _a.call(window, "event", "Bid-submitted", {
                    item_id: this.auctionItem.id,
                    bid_amount: maximumAmount,
                });
                this.fillAvailableMaxBids();
                events.onAuctionItemBid.trigger({
                    auctionItemId: this.auctionItem.id,
                });
                localStorage.setItem("forceReload", "true");
            }
            catch (error) {
                this.errorMessage = error.data.message;
            }
        },
        async buyNow() {
            try {
                await auctionV2ApiClient.bidLot(this.auctionItem.id, {
                    maximumAmount: this.auctionItem.estimatedPrice,
                    buyNow: true,
                });
                window.location.href = `${data.auctionUrl}${this.auctionItem.auctionId}`;
            }
            catch (error) {
                this.errorMessage = error.data.message;
            }
            finally {
                this.closeModal();
                this.isLoading = false;
            }
        },
        openBuyNowModal() {
            this.errorMessage = "";
            if (!document.body.dataset.username) {
                events.openLogin.trigger({ open: true, centeredFixed: true });
                return;
            }
            events.setBackdrop.trigger(true);
            this.modalOpen = true;
        },
        closeModal() {
            this.modalOpen = false;
            events.setBackdrop.trigger(false);
        },
        formatForeignCurrency(price) {
            return this.foreignCurrency && price
                ? formatForeignCurrency(price, this.foreignCurrency)
                : "";
        },
        get showForeignCurrency() {
            return (this.foreignCurrency && this.foreignCurrency.currency !== "NOK");
        },
        get isWaiting() {
            var _a;
            return ((_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.status) === AuctionItemStatus.Prepared
                || this.auctionItem.status === AuctionItemStatus.Scheduled;
        },
        get isBiddable() {
            var _a;
            return ((_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.status) === AuctionItemStatus.Available && !this.biddingHasEnded;
        },
        get isAftersale() {
            var _a;
            return ((_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.status) === AuctionItemStatus.NotSold
                && this.auctionItem.auctionStatus === AuctionStatus.Aftersale;
        },
        get biddingHasEnded() {
            var _a, _b;
            return ((_b = (_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.endAt) !== null && _b !== void 0 ? _b : "z") < new Date().toISOString();
        },
        get isSold() {
            var _a;
            return ((_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.status) === AuctionItemStatus.Sold
                || this.auctionItem.status === AuctionItemStatus.SoldConfirmed;
        },
        get showBidControls() {
            return this.isBiddable && this.liveScheduleReady;
        },
        get showLiveAuctionButton() {
            return this.isBiddable && this.isLiveAuction;
        },
        get liveAuctionUrl() {
            var _a;
            // Alpine reads the bound href before init() has filled the item in.
            if (((_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.id) == null)
                return "#";
            const price = parseInt(this.chosenMaxBid);
            return getLiveAuctionUrl(this.auctionItem.id, isNaN(price) ? null : price);
        },
        get canBid() {
            return this.chosenMaxBid != null || this.isAftersale;
        },
        get showBidStart() {
            var _a, _b;
            return (this.isWaiting &&
                new Date((_b = (_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.startAt) !== null && _b !== void 0 ? _b : 0) > new Date(0));
        },
        get showBidEnd() {
            var _a, _b;
            return (!this.showTimer &&
                new Date((_b = (_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.endAt) !== null && _b !== void 0 ? _b : 0) > new Date(0));
        },
        get showCurrentBidPrice() {
            return !this.isWaiting;
        },
        get showCustomerMaxBid() {
            var _a;
            return ((_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.customerBid) && this.isBiddable;
        },
        get showBidCount() {
            return !this.isWaiting;
        },
        get customerHasWinningBid() {
            var _a, _b;
            return ((_b = (_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.customerBid) === null || _b === void 0 ? void 0 : _b.hasWinningBid) === true;
        },
        get customerHasNotWinningBid() {
            var _a, _b;
            return ((_b = (_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.customerBid) === null || _b === void 0 ? void 0 : _b.hasWinningBid) === false;
        },
        get isCombinedType() {
            var _a;
            return (((_a = this.auctionItem) === null || _a === void 0 ? void 0 : _a.auctionType) != undefined &&
                this.auctionItem.auctionType ==
                    AuctionClassTypes.COMBINED.toString());
        },
        fillAvailableMaxBids() {
            var _a, _b;
            if (this.isBiddable) {
                this.availableMaxBids = getAvailableMaxBids(this.auctionItem.currentBidPrice, this.auctionItem.bidsCount, (_a = this.auctionItem.customerBid) === null || _a === void 0 ? void 0 : _a.maxBidPrice, (_b = data === null || data === void 0 ? void 0 : data.maxBids) !== null && _b !== void 0 ? _b : 0);
            }
            else {
                this.availableMaxBids = [];
            }
        },
    };
}
